import { useState } from "react";
import { ExternalLink, Link2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusDaysBadge } from "@/components/animals/StatusDaysBadge";
import { LabelWithHover } from "@/components/ui/LabelWithHover";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { useToast } from "@/hooks/use-toast";
import { formatReportDate, copyToClipboard } from "@/lib/utils";
import { formatSpeciesLabel } from "@/lib/score-utils";
import { TRACK_LABELS } from "@/lib/api/workflow-types";

// NOTE: No shared AnimalData type exists in apps/web/src — declared locally.
// Candidate for extraction to a shared types file when the drawer refactor is complete.
interface AnimalData {
  name: string | null;
  species: string | null;
  photoUrl: string | null;
  intakeDate?: string | null;
  currentStatus?: {
    kennelStatusNorm?: string | null;
    substatusNorm?: string | null;
    kennelStatusSince?: string | null;
    kennelStatusSinceIsApprox?: boolean;
    adoptionLevelNorm?: string | null;
  };
  evaluation?: {
    status: string | null;
    label: string | null;
    // S8 Step 1: days since animal entered current eval status (from animalActivityEvents)
    daysInStatus?: number | null;
  };
  // S8: Optional — caller populates from WorkflowDashboardItem.track.
  // AnimalDetailDrawer must pass item.track here; InteractionDetailDrawer leaves it absent.
  // This wiring is done in Step 6 when AnimalDetailDrawer adopts AnimalDetailsSection.
  track?: string | null;
}

interface AnimalDetailsSectionProps {
  animalId: string;
  animalData: AnimalData | null;
  isLoading: boolean;
}

export function AnimalDetailsSection({
  animalId,
  animalData,
  isLoading,
}: AnimalDetailsSectionProps) {
  const [entityLinkerOpen, setEntityLinkerOpen] = useState(false);
  const { toast } = useToast();

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const speciesLabel = animalData?.species ? formatSpeciesLabel(animalData.species) : "Unknown";
    const text = `${animalData?.name ?? animalId} (${speciesLabel}) - ${animalId} - ${window.location.origin}/animals/${animalId}`;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast({ title: "Copied", description: "Animal summary copied to clipboard." });
    } else {
      toast({ variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard. Please try again." });
    }
  };

  return (
    <div className="space-y-3">
      {/* Inline actions row — not full-width */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(`/animals/${animalId}`, "_blank")}
          data-testid="button-animal-details-view"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          View Profile
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEntityLinkerOpen(true)}
          data-testid="button-animal-details-add-to"
        >
          <Link2 className="h-3.5 w-3.5 mr-1.5" />
          Add to…
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          data-testid="button-animal-details-share"
        >
          <Share2 className="h-3.5 w-3.5 mr-1.5" />
          Share
        </Button>
      </div>

      {/* Fields — two-column grid: Intake + Status, Eval + Adoption level, Track full-width */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-border/40 pt-3">

        {/* Intake (left col) */}
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">Intake</span>
          {animalData?.intakeDate ? (
            <span>{formatReportDate(animalData.intakeDate)}</span>
          ) : isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Status (right col) — StatusDaysBadge renders label + days-in-status */}
        {/* items-start prevents the badge from stretching to full column width (flex-col default is align-items:stretch) */}
        <div className="flex flex-col gap-0.5 items-start">
          <span className="text-xs text-muted-foreground">Status</span>
          {animalData?.currentStatus?.kennelStatusNorm ? (
            <StatusDaysBadge
              status={animalData.currentStatus.kennelStatusNorm}
              substatus={animalData.currentStatus.substatusNorm}
              statusSince={animalData.currentStatus.kennelStatusSince}
              isApprox={animalData.currentStatus.kennelStatusSinceIsApprox}
              data-testid="animal-details-kennel-status-badge"
            />
          ) : isLoading ? (
            <Skeleton className="h-5 w-28" />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Eval status (left col) — day count folded inline */}
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">Eval status</span>
          {animalData?.evaluation ? (
            <span className="flex items-center gap-1">
              <LabelWithHover label={animalData.evaluation.label ?? animalData.evaluation.status ?? "No eval on record"} hoverContent={null} />
              {animalData.evaluation.daysInStatus != null
                && (animalData.evaluation.label ?? animalData.evaluation.status)
                && (
                <span className="text-muted-foreground">· {animalData.evaluation.daysInStatus}d</span>
              )}
            </span>
          ) : isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Adoption level (right col) — always rendered to preserve grid alignment;
            content is shown only when present or loading */}
        <div className="flex flex-col gap-0.5">
          {(animalData?.currentStatus?.adoptionLevelNorm != null || isLoading) && (
            <>
              <span className="text-xs text-muted-foreground">Adoption level</span>
              {animalData?.currentStatus?.adoptionLevelNorm ? (
                <span>{animalData.currentStatus.adoptionLevelNorm}</span>
              ) : (
                <Skeleton className="h-4 w-20" />
              )}
            </>
          )}
        </div>

        {/* Track — spans both columns; rendered only when present */}
        {animalData?.track && (
          <div className="flex flex-col gap-0.5 col-span-2">
            <span className="text-xs text-muted-foreground">Track</span>
            <span>{TRACK_LABELS[animalData.track as keyof typeof TRACK_LABELS] ?? animalData.track}</span>
          </div>
        )}

      </div>

      <EntityLinkerDialog
        open={entityLinkerOpen}
        onOpenChange={setEntityLinkerOpen}
        entityType="animal"
        entityId={animalId}
        entityLabel={animalData?.name ?? undefined}
        animalId={animalId}
        animalSpecies={animalData?.species ?? undefined}
        animalPhotoUrl={animalData?.photoUrl ?? null}
      />
    </div>
  );
}
